<template>
  <v-layout :theme="theme">
    <v-app>
      <v-app-bar border="b" flat height="56">
        <!-- Mode Selector -->
        <ModeSelector class="ml-2" />

        <v-divider vertical class="mx-2" />

        <!-- Dynamic menu buttons based on mode -->
        <div class="d-flex align-center overflow-x-auto overflow-y-hidden menu-strip" style="max-width: calc(100% - 200px);">
          <v-btn
            v-for="item in currentMenuItems"
            :key="item.name"
            :to="{ name: item.name }"
            class="text-none"
            :icon="$vuetify.display.xsOnly"
            :active="$route.name === item.name"
            :min-width="$vuetify.display.xsOnly ? 40 : undefined"
            density="compact"
            @dblclick.prevent="item.name === 'Update' && copyQueryString()"
          >
            <img
              v-if="item.image"
              :src="item.image"
              alt=""
              width="24"
              height="24"
              class="menu-item-image"
            />
            <v-icon v-else size="20">{{ item.icon }}</v-icon>
            <span
              v-if="$vuetify.display.smAndUp"
              class="ms-1 text-body-2"
            >
              {{ $t(item.text) }}
            </span>
          </v-btn>
        </div>

        <v-spacer></v-spacer>

        <v-btn 
          icon 
          density="compact"
          class="ms-auto"
          style="min-width: 40px;"
        >
          <v-menu location="bottom end" :close-on-content-click="true">
            <template v-slot:activator="{ props }">
              <v-btn icon v-bind="props" density="compact">
                <v-icon size="20">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item @click="toggleTheme">
                <template v-slot:prepend>
                  <v-icon>mdi-theme-light-dark</v-icon>
                </template>
                <v-list-item-title>
                  Toggle Theme
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="showSettingsDialog = true">
                <template v-slot:prepend>
                  <v-icon>mdi-cog</v-icon>
                </template>
                <v-list-item-title>
                  Settings
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-btn>
      </v-app-bar>

      <v-main>
        <div class="content-container">
          <router-view></router-view>

          <v-dialog v-model="state.showWarningDialog" persistent max-width="400">
            <v-card>
              <v-card-title class="text-h5">
                Missing Parameters
              </v-card-title>
              <v-card-text>
                The following required parameters are missing from the URL:
                <ul class="mt-2">
                  <li v-for="param in state.missingParams" :key="param" class="mt-1">
                    {{ param }}
                  </li>
                </ul>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="error" @click="handleMissingParams">
                  Close
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <WelcomeDialog
            :api-progress="apiProgress"
            :total-api-requests="totalApiRequests"
            :with-loading="true"
          />

          <AppSettingsDialog v-model="showSettingsDialog" />

          <!-- Toast Notification for Query String Copy -->
          <v-snackbar
            v-model="showCopyToast"
            :timeout="2000"
            color="success"
            location="top"
          >
            Dev mode
          </v-snackbar>
        </div>
      </v-main>
    </v-app>
  </v-layout>
</template>

<script setup>
import { reactive, computed, watch, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useTheme } from 'vuetify';
import { useAppStore } from '@/stores/app';
import { useSettingsStore } from '@/stores/settings';
import { useScheduleStore } from '@/stores/schedule';
import { useTaskStore } from '@/stores/task';
import { useResourceStore } from '@/stores/resource';
import WelcomeDialog from './components/dialogs/WelcomeDialog.vue';
import AppSettingsDialog from './components/dialogs/AppSettingsDialog.vue';
import ModeSelector from './components/ModeSelector.vue';
import SchedulePermissions from './utils/SchedulePermissions.js';
import { formatLocationDisplayTitle } from '@/utils/LocationDisplay';
// Same asset as the main SPA's RPilotLauncher (icon-dark.svg).
import rpilotIcon from '@/assets/rpilot-icon.svg';
import { isRPilotEnabledForApiUrl } from '@/config/rpilotWhitelist';

const appStore = useAppStore();
const settingsStore = useSettingsStore();
const scheduleStore = useScheduleStore();
const taskStore = useTaskStore();
const resourceStore = useResourceStore();
const router = useRouter();
const vuetifyTheme = useTheme();
const currentTheme = computed(() => appStore.theme);

const filteredMenuItems = ref([]);

// RPilot is only offered on whitelisted API hosts (see src/config/rpilotWhitelist.js).
const rpilotEnabled = computed(() => isRPilotEnabledForApiUrl(settingsStore.settings.apiUrl));

// Task Set Profile menu items (not permission-filtered for now)
const taskSetProfileMenuItems = computed(() => {
  const items = [
    { name: 'TSPUpdate', icon: 'mdi-file-import', text: 'Update' },
    { name: 'TSPCreate', icon: 'mdi-file-plus', text: 'Create' },
  ];
  if (rpilotEnabled.value) {
    items.push({ name: 'TSPRPilot', icon: 'mdi-robot', image: rpilotIcon, text: 'RPilot' });
  }
  return items;
});

// Computed menu items based on current app mode
const currentMenuItems = computed(() => {
  if (appStore.appMode === 'taskSetProfiles') {
    return taskSetProfileMenuItems.value;
  }
  return filteredMenuItems.value;
});

const getUserColorScheme = () => {
  if (window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      vuetifyTheme.global.name.value = 'dark'; //set immediately
      return 'dark';
    } else {
      return 'light';
    }
    
  } else {
    return currentTheme.value;
  }
};

const theme = ref(getUserColorScheme());
appStore.setTheme(theme.value);

const state = reactive({
  showWarningDialog: false,
  missingParams: [],
});

const buildMode = ref('production');
const showCopyToast = ref(false);
const showSettingsDialog = ref(false);

const apiProgress = ref(0);
const totalApiRequests = 4;

const settings = computed(() => settingsStore.settings);
const loadDashboard = computed(() => appStore.getLoadDashboard);

let permissions = null;



const requiredParams = [
  'scopedToken',
  'projectName',
  'projectCode',
  'locale',
  'apiUrl'
];
const parseQuerystring = () => {

  const urlParams = new URLSearchParams(window.location.search);
  const params = {
      scopedToken: urlParams.get('scopedToken'),
      projectName: urlParams.get('projectName'),
      projectCode: urlParams.get('projectCode'),
      locale: urlParams.get('locale'),
      apiUrl: urlParams.get('apiUrl')
  };

  // Validate parameters
  state.missingParams = requiredParams.filter(param => !params[param]);

  if (state.missingParams.length > 0) {
    state.showWarningDialog = true;
    console.error('Missing required parameters:', state.missingParams);
    return false;
  }

  // If all parameters are present, store them
  settingsStore.setValues({
    ...params
  });

  return true;
};


const getData = async () => {
  try {
    apiProgress.value = 0;
    appStore.setDataLoaded(false);

    const apiRequests = [
      appStore.apiClient.get('/api/users/current'),
      appStore.apiClient.get('/api/' + settings.value.projectCode + '/schedules'),
      appStore.apiClient.get('/api/schedule-templates'),
      appStore.apiClient.get(`/api/${settingsStore.settings.projectCode}/task-set-profiles`),
      appStore.apiClient.get(`/api/issue-templates?published=true`),
      appStore.apiClient.get(`/api/process-templates?published=true`),
      appStore.apiClient.get(`/api/${settingsStore.settings.projectCode}/locations`),
      appStore.apiClient.get(`/api/${settingsStore.settings.projectCode}/drawing-sets`),
      appStore.apiClient.get(`/api/${settingsStore.settings.projectCode}/users/current/permission-rules`),
      appStore.apiClient.get(`/api/${settingsStore.settings.projectCode}/teams`),
      appStore.apiClient.get(`/api/${settingsStore.settings.projectCode}/reporting/project-item-templates`),
      // Latest PUBLISHED revision per calendar family: without the filter the
      // API returns each family's newest row, which is a draft whenever one
      // exists and would hide that family's published calendar.
      appStore.apiClient.get(`/api/${settingsStore.settings.projectCode}/calendars?published=true`)
    ];

    // Use Promise.allSettled to run all requests concurrently
    const results = await Promise.allSettled(
      apiRequests.map(request =>
        request.then(response => {
          apiProgress.value += 1;
          return response;
        })
      )
    );

    // Extract successful results
    const successfulResults = results
      .filter(result => result.status === 'fulfilled')
      .map(result => result.value);

    // Check if there are errors
    const failedResults = results.filter(result => result.status === 'rejected');
    if (failedResults.length > 0) {
      console.error('Some API requests failed:', failedResults);
    }

    // Destructure the successful results (note: currentUser already fetched in warmup)
    const [
      currentUser,
      schedules,
      scheduleTemplates,
      taskSetProfilesData,
      issueTemplatesData,
      processTemplatesData,
      locationsData,
      drawingSetsData,
      permissionRulesData,
      teamsData,
      itemTemplatesData,
      calendarsData
    ] = successfulResults;

    settingsStore.setUserId(currentUser.id);

    resourceStore.setTeams(teamsData || []);

    // Fetch user details to get defaultTeamId
    try {
      const userDetails = await appStore.apiClient.get('/api/' + settings.value.projectCode + '/users/' + currentUser.id);
      if (userDetails?.defaultTeamId) {
        settingsStore.setDefaultTeamId(userDetails.defaultTeamId);
        // Get team tags for permissions
        const userTeam = teamsData.find(team => team.id === userDetails.defaultTeamId);
        if (userTeam) {
          resourceStore.setTeamTags(userTeam.tags || []);
        }
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }

    if (buildMode.value === 'webapp') {
      await settingsStore.updateLocale(currentUser.locale);
    }

    const scheduleTemplatesObj = scheduleTemplates.reduce((acc, scheduleTemplate) => ({
        ...acc,
        [scheduleTemplate.id]: [...(acc[scheduleTemplate.id] || []), scheduleTemplate]
    }), {});

    // Process task set profiles
    const taskSetProfiles = taskSetProfilesData
      .filter(tsp => tsp.isPublished && tsp.current)
      .map(tsp => ({
        title: `${tsp.code} - ${tsp.title}`,
        code: tsp.code,
        profile: tsp
      }));

    const scheduleTemplatesCurrent = scheduleTemplates.filter(st => st.current);

    // Process locations
    const drawingSetsArray = Array.isArray(drawingSetsData) ? drawingSetsData : [drawingSetsData];

    // First pass: count locations with same title under same drawing to detect duplicates
    const locationKeyCount = {};
    for (let i = 0; i < locationsData.length; i++) {
      const loc = locationsData[i];
      // Key by title + drawingId to detect duplicates under the same drawing
      const key = `${loc.title}|${loc.drawingId}`;
      locationKeyCount[key] = (locationKeyCount[key] || 0) + 1;
    }

    // Second pass: build locationList, adding x,y only for duplicates
    const locationList = [];
    for (let i = 0; i < locationsData.length; i++) {
      const loc = locationsData[i];
      const drawingSet = drawingSetsArray.find(ds => ds.id === loc.drawingSetId);
      if (drawingSet) {
        const key = `${loc.title}|${loc.drawingId}`;
        const hasDuplicates = locationKeyCount[key] > 1;

        // Same formatter the RPilot sandbox's location.displayTitle uses —
        // a transform matching a spreadsheet column against project locations
        // has to be comparing byte-identical strings. x,y only for duplicates.
        locationList.push({
          location: formatLocationDisplayTitle(loc, drawingSet.title, { withCoordinates: hasDuplicates }),
          id: loc.id
        });
      }
    }

    // Store all the processed data
    scheduleStore.setSchedules(schedules);
    scheduleStore.setScheduleTemplates(scheduleTemplatesCurrent);
    scheduleStore.setScheduleTemplatesObj(scheduleTemplatesObj);
    taskStore.setTaskSetProfiles(taskSetProfiles);
    resourceStore.setDrawingSets(drawingSetsArray);
    resourceStore.setLocations(locationsData);
    resourceStore.setLocationsList(locationList);

    // Extract permission rules array from object (shape: { "userId": [] })
    const permissionRulesArray = permissionRulesData ? Object.values(permissionRulesData)[0] || [] : [];
    resourceStore.setPermissionRules(permissionRulesArray);
    taskStore.setItemTemplates(itemTemplatesData || []);

    // calendarsData may be an object keyed by index — normalize to array
    const calendarsArray = calendarsData
      ? (Array.isArray(calendarsData) ? calendarsData : Object.values(calendarsData))
      : [];
    const publishedCalendars = calendarsArray.filter(c => c.current && c.isPublished);
    scheduleStore.setCalendars(publishedCalendars);

    // Note: setDataLoaded is now called AFTER setupAppMenu sets defaultRoute
    appStore.setShowWelcomeDialog(false);
    appStore.setLoadDashboard(false);

  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

const toggleTheme = () => {
  theme.value = theme.value === 'dark' ? 'light' : 'dark';
  appStore.setTheme(theme.value);
};

const copyQueryString = async () => {
  const queryString = window.location.search;
  navigator.clipboard.writeText(queryString).then(() => {
    showCopyToast.value = true;
  }).catch((err) => {
    console.error('Failed to copy query string:', err);
  });
};

const setupAppPermissions = () => {
  permissions = new SchedulePermissions({
    permissionRules: [...resourceStore.permissionRules],
    userId: settingsStore.settings.userId,
    teamId: settingsStore.settings.defaultTeamId, // Start with default team
    defaultTeamId: settingsStore.settings.defaultTeamId,
    teamTags: [...resourceStore.teamTags]
  });
};

const setupAppMenu = () => {
  if (!permissions) {
    return;
  }

  // Clear existing menu items
  filteredMenuItems.value = [];

  const hasPageAccess = permissions.canAccessSchedulePage();
  const canCreate = permissions.canCreateSchedule();
  const hasSchedules = scheduleStore.schedules && scheduleStore.schedules
    .filter(schedule => schedule.current)
    .some(schedule => permissions.canReadSchedule(schedule) && permissions.canEditSchedule(schedule));

  // User must have page access to see any page (unless schedules exist)
  if (!hasPageAccess) {
    appStore.setDefaultRoute('NoPermissions');
    return;
  }

  // Determine the default route based on permissions and schedules
  let defaultRoute = null;

  // Add Update menu item only if there's at least one schedule
  if (hasSchedules) {
    filteredMenuItems.value.push({ name: 'Update', icon: 'mdi-file-tree', text: 'Update' });
    defaultRoute = 'Update'; // Update is the default when available
  }

  // Add Create menu item if user can create schedules
  if (canCreate) {
    filteredMenuItems.value.push({ name: 'Create', icon: 'mdi-calendar-plus', text: 'Create' });
    // If no default route set yet (no Import), Create becomes the default
    if (!defaultRoute) {
      defaultRoute = 'Create';
    }
  }

  // Add Link menu item only if user has subtask link access
  // Pass all current schedules to check if at least one can have links updated
  const currentSchedules = scheduleStore.schedules?.filter(schedule => schedule.current) || [];
  if (permissions.canAccessLinkPage(currentSchedules)) {
    filteredMenuItems.value.push({ name: 'Link', icon: 'mdi-link', text: 'Link' });
  }

  // RPilot is gated on the same schedule-page access check used for Update/Create —
  // it can mint new schedules or edit existing ones, so anyone with page access can use it.
  // It is additionally restricted to whitelisted API hosts.
  if (rpilotEnabled.value) {
    filteredMenuItems.value.push({ name: 'RPilot', icon: 'mdi-robot', image: rpilotIcon, text: 'RPilot' });
  }

  // Set the default route in store - the router guard will handle navigation
  appStore.setDefaultRoute(defaultRoute);
};

const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
colorSchemeQuery.addEventListener('change', (e) => {
  theme.value = e.matches ? 'dark' : 'light';
  vuetifyTheme.global.name.value = theme.value;
});

watch(
  () => [
    settings.value.scopedToken, 
    settings.value.projectCode,
    loadDashboard.value
  ],
  ([newScopedToken, newProjectCode, newLoadDashboard]) => {
    if (newScopedToken && newProjectCode && newLoadDashboard) {
      getData();
    }
  }
);


watch(() => currentTheme.value, (newValue) => {
  vuetifyTheme.global.name.value = newValue;
});

// Watch for defaultRoute changes and navigate when set
watch(() => appStore.defaultRoute, (newRoute) => {
  if (newRoute && router.currentRoute.value.name === 'Root') {
    router.push({ name: newRoute });
  }
});

// Watch for schedule changes and re-evaluate menu
watch(() => scheduleStore.schedules, () => {
  if (appStore.dataLoaded && permissions) {
    setupAppMenu();
  }
}, { deep: true });

// Watch for route changes to sync appMode (deep links included)
watch(() => router.currentRoute.value.path, (path) => {
  if (path.startsWith('/taskset-profiles/')) {
    appStore.setAppMode('taskSetProfiles');
  } else if (path === '/' || path === '/rpilot' || path.startsWith('/schedules/')) {
    appStore.setAppMode('schedules');
  }
}, { immediate: true });

const updateTheme = (e) => {
  theme.value = e.matches ? 'dark' : 'light';
  appStore.setTheme(theme.value);
};

onMounted(async () => {
  const isWebApp = import.meta.env.MODE === 'webapp';
  if (isWebApp) {
    buildMode.value = 'webapp';
  }

  const queryParamsValid = parseQuerystring();
  if (!queryParamsValid) {
    return; // Stop further initialization if parameters are invalid
  }

  await getData();

  setupAppPermissions();
  setupAppMenu();

  // Set dataLoaded AFTER setupAppMenu sets defaultRoute
  // This ensures the router guard has the correct defaultRoute when it triggers
  appStore.setDataLoaded(true);

  appStore.updateBuildMode(buildMode.value);
  appStore.setTheme(theme.value);
  colorSchemeQuery.addEventListener('change', updateTheme);
});

onUnmounted(() => {
  colorSchemeQuery.removeEventListener('change', updateTheme);
});
</script>

<style>

.content-container {
  padding: 16px;
  overflow-x: auto;
}

/* overflow-x alone forces overflow-y to auto, so any sub-pixel overflow
   shows scrollbars on Windows (classic scrollbars take layout space). Keep
   horizontal scrolling for narrow frames but never draw the bar. */
.menu-strip {
  scrollbar-width: none;
}

/* Image-based menu icons (RPilot) sit where a v-icon normally would. Keep
   within the 24px compact button height. The artwork sits low inside the SVG
   viewBox, so lift it slightly to optically center against the label. */
.menu-item-image {
  display: block;
  flex-shrink: 0;
  transform: translateY(-1px);
}

.fab-container {
  position: fixed;
  bottom: 16px;
  right: 16px;
}

.logo-btn {
  padding: 0 0 0 10px!important;
  min-width: 36px !important;
  width: 80px !important;
}

.logo-img {
  max-width: 80px;
  object-fit: contain;
}

.value-text {
  color: black;
}

.title-text {
  color: white;
}

.v-card {
  margin: 0 auto;
}

.margin-bottom {
  margin-bottom: 16px;
}

.v-chip {
  padding: 0 4px !important;
}

/* Vuetify tints the overlay scrim with on-surface, which reads as a light
   haze on the charcoal dark theme. Use a dark scrim instead, matching the
   host RDrive shell. */
.v-theme--dark .v-overlay__scrim {
  background: #000;
}


</style>